import {
  Component,
  Inject,
  LOCALE_ID,
  OnInit,
  ChangeDetectionStrategy,
  model,
  input,
  signal,
  output,
  Output,
  EventEmitter,
} from '@angular/core';
import { GoogleSigninButtonModule, SocialAuthService } from '@abacritt/angularx-social-login';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { socialAuthServiceConfig } from './social-auth-service-config';
import { SocialLoginResponse, UserService } from '@portal-front/portal-auth-data-access';
import { finalize } from 'rxjs';
import { GoogleSigninButtonDirective } from '@abacritt/angularx-social-login/directives/google-signin-button.directive';
import { EfDrawerComponent, EfSpinnerComponent } from '@portal-front/design-system';
import { EfSocialRegisterFormComponent } from '@portal-front/portal-auth-feature-social-register-form';
import { EfDeferErrorComponent } from '../defer-error/defer-error.component';

@Component({
  selector: 'ef-login-google-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GoogleSigninButtonModule,
    EfDrawerComponent,
    EfSocialRegisterFormComponent,
    EfSpinnerComponent,
    EfDeferErrorComponent,
  ],
  providers: [socialAuthServiceConfig, SocialAuthService],
  templateUrl: './login-google-button.component.html',
  styleUrls: ['./login-google-button.component.scss'],
})
@UntilDestroy()
export class LoginGoogleButtonComponent implements OnInit {
  @Output() success = new EventEmitter<SocialLoginResponse>();
  @Output() error = new EventEmitter<SocialLoginResponse>(); // eslint-disable-line @angular-eslint/no-output-native

  public readonly googleAuthToken = signal<string>('');
  public readonly showSocialRegistration = signal<boolean>(false);

  public readonly loading = model(false);
  public readonly text = input<GoogleSigninButtonDirective['text']>('signin_with');

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private authService: SocialAuthService,
    private userService: UserService,
  ) {}

  public ngOnInit(): void {
    this.authService.authState
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: async (user) => {
          if (user) {
            this.loading.set(true);

            const response = await this.userService.googleLogin(user.idToken);

            if (response.success) {
              this.success.emit(response);
            } else {
              if (response.message === 'SOCIAL_LOGIN_USER_NOT_EXISTS_ERROR' && response.token) {
                this.googleAuthToken.set(response.token);
                this.showSocialRegistration.set(true);
              }

              this.error.emit(response);
            }

            this.loading.set(false);
          }
        },
        error: (error) => {
          this.error.emit({
            success: false,
            message: error.message,
          });
        },
      });
  }

  public onSocialRegistrationRejected(): void {
    this.showSocialRegistration.set(false);
    this.error.emit();
  }

  public onSocialRegistrationSuccess(): void {
    this.success.emit();
  }
}
